/**
 * MovementDebugSection — the Debug menu's "Movement" panel: the live
 * movement-health numbers behind a "rubber-band" report, refreshed every
 * second from the probe surfaces the avatar + physics modules already keep:
 *   - ack lag (last / max) of the lag-aware reconcile and its decisions
 *     (predicted / current / waiting bases, smooth / hard corrections),
 *   - the unstick watchdog (scans / trapped / relocations / wedged) and the
 *     server nudges it sent (accepted / rejected / reverted / timed out),
 *   - local vs server row positions and the input sequence gap.
 * Registered by `MovementDebugModule` right after the Debug menu.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import type { AtlasDebugMenuModule } from './AtlasDebugMenuModule.js';

interface ReconDbg { runs: number; grace: number; local: number[]; recon: { none: number; smooth: number; hard: number; predicted: number; current: number; waiting: number; lastDiv: number; lastAckLagMs: number; maxAckLagMs: number }; inFlight: number }
interface UnstickDbg { scans: number; trapped: number; relocations: number; depen: number; wedged: number; last: { x: number; z: number } | null; nudges?: { sent: number; accepted: number; rejected: number; reverted: number; timedOut: number; lastError: string } }

export function movementSnapshot(): Record<string, unknown> {
  const g = globalThis as unknown as { __reconcileDbg?: () => ReconDbg; __unstick?: UnstickDbg; __physDiag?: () => { maxRawMps: number; capped: number } };
  const r = g.__reconcileDbg?.();
  const u = g.__unstick;
  const me = atlas.conn?.identity ? atlas.conn.db.playerState.identity.find(atlas.conn.identity) : null;
  return {
    local: [Math.round(atlas.localX * 10) / 10, Math.round(atlas.localZ * 10) / 10],
    row: me ? [Math.round(me.posX * 10) / 10, Math.round(me.posZ * 10) / 10] : null,
    rowGapM: me ? Math.round(Math.hypot(me.posX - atlas.localX, me.posZ - atlas.localZ) * 10) / 10 : null,
    seqLocal: atlas.inputSeq, seqAcked: me?.inputSeq ?? null,
    ackLagMs: r ? Math.round(r.recon.lastAckLagMs) : null, maxAckLagMs: r ? Math.round(r.recon.maxAckLagMs) : null,
    corrections: r ? { smooth: r.recon.smooth, hard: r.recon.hard, waiting: r.recon.waiting, lastDiv: Math.round(r.recon.lastDiv * 100) / 100, grace: r.grace } : null,
    unstick: u ? { scans: u.scans, trapped: u.trapped, relocations: u.relocations, wedged: u.wedged, depen: u.depen, nudges: u.nudges ?? null } : null,

    phys: g.__physDiag?.() ?? null,
  };
}

export function registerMovementDebugSection(menu: AtlasDebugMenuModule): void {
  menu.registerSection('Movement', (body) => {
    body.id = 'debug-movement-section';
    const pre = document.createElement('pre');
    pre.style.cssText = 'margin:0;font:11px/1.35 ui-monospace,monospace;color:#cfd8e6;white-space:pre-wrap';
    body.appendChild(pre);
    const render = (): void => {
      const s = movementSnapshot();
      const c = s.corrections as { smooth: number; hard: number; waiting: number; lastDiv: number; grace: number } | null;
      const u = s.unstick as { scans: number; trapped: number; relocations: number; wedged: number; nudges: { sent: number; accepted: number; rejected: number; reverted: number; timedOut: number; lastError: string } | null } | null;
      pre.textContent = [
        `local ${JSON.stringify(s.local)}  row ${JSON.stringify(s.row)}  gap ${s.rowGapM ?? '?'} m`,
        `seq local ${s.seqLocal} / acked ${s.seqAcked ?? '?'}  ack lag ${s.ackLagMs ?? '?'} ms (max ${s.maxAckLagMs ?? '?'})`,
        c ? `corrections smooth ${c.smooth} · hard ${c.hard} · waiting ${c.waiting} · last div ${c.lastDiv} m · grace ${c.grace}` : 'reconcile: —',
        u ? `unstick scans ${u.scans} · trapped ${u.trapped} · relocations ${u.relocations} · wedged ${u.wedged}` : 'unstick: —',
        s.phys ? `capsule max ${(s.phys as { maxRawMps: number; capped: number }).maxRawMps} m/s tried · step cap engaged ${(s.phys as { capped: number }).capped}×` : 'physics: —',
        u?.nudges ? `nudges sent ${u.nudges.sent} · ok ${u.nudges.accepted} · rejected ${u.nudges.rejected} · reverted ${u.nudges.reverted} · timed out ${u.nudges.timedOut}${u.nudges.lastError ? ` · last: ${u.nudges.lastError}` : ''}` : 'nudges: none yet',
      ].join('\n');
    };
    render();
    window.setInterval(() => { if (body.offsetParent !== null) render(); }, 1000); // only while the panel is open
    (window as unknown as Record<string, unknown>).__movementDebug = { snapshot: movementSnapshot };
  });
}

/** Registers the section once the Debug menu exists (main.ts orders it after AtlasDebugMenuModule). */
export class MovementDebugModule implements IModule {
  readonly name = 'MovementDebug';
  init(app: App): void {
    const menu = app.getModule<AtlasDebugMenuModule>('DebugMenu');
    if (menu) registerMovementDebugSection(menu);
  }
}
